import { dssChainData, dssConfigFor } from '@1delta/data-sdk'
import { UsddCdpManagerAbi, UsddProxyRegistryAbi } from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import { hexToString, type Hex } from 'viem'
import { Call } from '../../../utils/multicall'
import { dssIlkBytes32 } from '../../public-data/dss/fetchPublic'

/**
 * User-data call build for any dss deployment (Sky / the USDD fork). CDPs are
 * DssCdpManager ids —
 * MANY per user per ilk — held as a per-owner doubly-linked list, so this is
 * a DISCOVERY-first build (async, same pattern as Teller's bidId scan):
 *
 *  1. `manager.count/first` for the account AND for the account's DSProxy
 *     (`ProxyRegistry.proxies(account)`) — the stock ProxyActions one-shots
 *     open CDPs OWNED BY THE PROXY, so walking only the EOA's list would
 *     miss every position opened through the standard route;
 *  2. walk each list — one small multicall per node reading
 *     `urns(cdp)` / `ilks(cdp)` / `list(cdp)` — until `next == 0`;
 *  3. keep CDPs whose ilk is in the curated market roster;
 *  4. data phase: `Vat.urns(ilk32, urn)` per kept CDP, then one
 *     `Vat.ilks(ilk32)` per unique ilk at the tail (the rate leg —
 *     debt = art × rate).
 *
 * The discovered layout is stashed in a short-lived module cache so the
 * parser (a separate phase fed only the multicall results) can slice it —
 * same trick as Teller / Liquity / the Lista broker caches.
 *
 * With the roster empty (USDD's standing EVM state — no ilk filed, see
 * USDD.md) this returns [] after zero RPC calls. Even with a full roster
 * (Sky's 8 Ethereum ilks) the common no-position case costs ONE 3-call
 * multicall.
 */
export const DSS_CALLS_PER_CDP = 1

/** Defensive walk bound — a user with more CDPs than this gets truncated
 *  discovery (logged), never a hang. Maker power users hold a handful. */
const MAX_CDP_WALK = 64

const CACHE_TTL_MS = 5 * 60_000

export interface DssDiscoveredCdp {
  /** decimal uint256 CDP id — the sub-account id. */
  cdpId: string
  /** The urn handle positions are keyed by in the Vat. */
  urn: string
  /** Ilk string from the curated roster (e.g. `WBTC-A`). */
  ilk: string
}

export interface DssDiscovery {
  cdps: DssDiscoveredCdp[]
  /** Unique ilks of the kept CDPs, in tail-call order. */
  ilks: string[]
  at: number
}

const discoveryCache = new Map<string, DssDiscovery>()
// The LENDER is part of the key: two dss brands (SKY and USDD) can be live on
// the same chain for the same account, and they are INDEPENDENT Maker stacks
// with their own CDP managers — a chain-only key would let one brand's walk
// overwrite the other's and the parser would attribute the wrong CDPs.
const cacheKey = (lender: string, chainId: string, account: string) =>
  `${lender}:${chainId}:${account.toLowerCase()}`

export const getCachedDssCdps = (
  lender: string,
  chainId: string,
  account: string,
): DssDiscovery | undefined => {
  const hit = discoveryCache.get(cacheKey(lender, chainId, account))
  if (!hit || Date.now() - hit.at > CACHE_TTL_MS) return undefined
  return hit
}

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' || typeof v === 'string') return BigInt(v)
  } catch {}
  return 0n
}

const field = (res: any, name: string, idx: number): any =>
  res?.[name] ?? res?.[idx]

export const buildDssUserCall = async (
  chainId: string,
  lender: string,
  account: string,
): Promise<Call[]> => {
  const cfg = dssConfigFor(lender, chainId)
  const markets = dssChainData(lender, chainId)?.markets ?? []
  const stash = (d: DssDiscovery) =>
    discoveryCache.set(cacheKey(lender, chainId, account), d)

  if (!cfg || markets.length === 0) {
    stash({ cdps: [], ilks: [], at: Date.now() })
    return []
  }
  const curated = new Set(markets.map((m) => m.ilk))

  // Phase 1 — heads of the per-owner linked lists for the account AND its
  // DSProxy: the ProxyActions one-shots open CDPs owned by the PROXY, while
  // direct manager opens are EOA-owned, so both lists carry real positions.
  const zero = '0x0000000000000000000000000000000000000000'
  const owners: { owner: string; count: bigint; cursor: bigint }[] = []
  try {
    const [rawCount, rawFirst, rawProxy] = (await multicallRetryUniversal({
      chain: chainId,
      abi: [...UsddCdpManagerAbi, ...UsddProxyRegistryAbi] as any,
      calls: [
        { address: cfg.cdpManager, name: 'count', params: [account] },
        { address: cfg.cdpManager, name: 'first', params: [account] },
        { address: cfg.proxyRegistry, name: 'proxies', params: [account] },
      ],
      allowFailure: false,
    })) as [bigint, bigint, string]
    owners.push({ owner: account, count: big(rawCount), cursor: big(rawFirst) })
    const proxy = String(rawProxy ?? zero)
    if (proxy.toLowerCase() !== zero) {
      const [pCount, pFirst] = (await multicallRetryUniversal({
        chain: chainId,
        abi: UsddCdpManagerAbi as any,
        calls: [
          { address: cfg.cdpManager, name: 'count', params: [proxy] },
          { address: cfg.cdpManager, name: 'first', params: [proxy] },
        ],
        allowFailure: false,
      })) as [bigint, bigint]
      owners.push({ owner: proxy, count: big(pCount), cursor: big(pFirst) })
    }
  } catch {
    // Discovery failure ⇒ report nothing rather than a fake empty book;
    // the stale-cache TTL keeps a prior good discovery alive for 5 min.
    return []
  }

  const live = owners.filter((o) => o.count > 0n && o.cursor !== 0n)
  if (live.length === 0) {
    stash({ cdps: [], ilks: [], at: Date.now() })
    return []
  }

  // Phase 2 — walk each list (one 3-call multicall per node; a linked list
  // cannot be parallelised, but per-owner counts are tiny).
  const cdps: DssDiscoveredCdp[] = []
  try {
    for (const head of live) {
      let cursor = head.cursor
      const steps = Number(
        head.count > BigInt(MAX_CDP_WALK) ? MAX_CDP_WALK : head.count,
      )
      if (head.count > BigInt(MAX_CDP_WALK)) {
        console.warn(
          `${lender}: owner ${head.owner} holds ${head.count} CDPs on chain ${chainId} — truncating discovery at ${MAX_CDP_WALK}`,
        )
      }
      for (let i = 0; i < steps && cursor !== 0n; i++) {
        const [urn, ilk32, link] = (await multicallRetryUniversal({
          chain: chainId,
          abi: UsddCdpManagerAbi as any,
          calls: [
            { address: cfg.cdpManager, name: 'urns', params: [cursor] },
            { address: cfg.cdpManager, name: 'ilks', params: [cursor] },
            { address: cfg.cdpManager, name: 'list', params: [cursor] },
          ],
          allowFailure: false,
        })) as [string, string, any]
        // bytes32 → trimmed ascii ilk (worker-safe — no Buffer).
        let ilk = ''
        try {
          ilk = hexToString(ilk32 as Hex, { size: 32 }).replace(/[\s\0]+$/g, '')
        } catch {}
        if (curated.has(ilk)) {
          cdps.push({ cdpId: cursor.toString(), urn: String(urn), ilk })
        }
        cursor = big(field(link, 'next', 1))
      }
    }
  } catch {
    return []
  }

  const ilks = [...new Set(cdps.map((c) => c.ilk))]
  stash({ cdps, ilks, at: Date.now() })
  if (cdps.length === 0) return []

  // Phase 3 — the data-call layout the parser slices:
  //   per CDP:        Vat.urns(ilk32, urn)
  //   tail, per ilk:  Vat.ilks(ilk32)      (rate — debt = art × rate)
  const calls: Call[] = cdps.map((c) => ({
    address: cfg.vat,
    name: 'urns',
    params: [dssIlkBytes32(c.ilk), c.urn],
  }))
  for (const ilk of ilks) {
    calls.push({
      address: cfg.vat,
      name: 'ilks',
      params: [dssIlkBytes32(ilk)],
    })
  }
  return calls
}
